/**
 * JWT-based AuthProvider — replaces FirebaseClientProvider.
 * Same context API shape so all child components continue to work.
 */
'use client';

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { authApi, tokenStore } from '@/lib/api';
import type { LoginResponse } from '@/lib/api/auth';

function installLocalApiFetchNormalizer() {
    if (typeof window === 'undefined') return;
    const markerKey = '__s360FetchLocalApiNormalized';
    const anyWindow = window as any;
    if (anyWindow[markerKey]) return;

    const localApiPort = '8000';
    const originalFetch = window.fetch.bind(window);

    const normalizeApiUrl = (urlLike: string): string => {
        try {
            const parsed = new URL(urlLike, window.location.origin);
            const isLocalHost = parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1';
            if (!isLocalHost) return parsed.toString();
            if (!parsed.pathname.startsWith('/api/')) return parsed.toString();
            if (!parsed.port || parsed.port === localApiPort) return parsed.toString();
            // Keep frontend same-origin API routes untouched.
            if (parsed.port === window.location.port) return parsed.toString();
            parsed.port = localApiPort;
            return parsed.toString();
        } catch {
            return urlLike;
        }
    };

    window.fetch = (async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
        if (typeof input === 'string') {
            return originalFetch(normalizeApiUrl(input), init);
        }
        if (input instanceof URL) {
            return originalFetch(normalizeApiUrl(input.toString()), init);
        }
        if (typeof Request !== 'undefined' && input instanceof Request) {
            const normalizedUrl = normalizeApiUrl(input.url);
            if (normalizedUrl === input.url) {
                return originalFetch(input, init);
            }
            const cloned = new Request(normalizedUrl, input);
            return originalFetch(cloned, init);
        }
        return originalFetch(input as RequestInfo, init);
    }) as typeof window.fetch;

    anyWindow[markerKey] = true;
}

installLocalApiFetchNormalizer();

type User = LoginResponse['user'] | null;

interface AuthContextValue {
    user: User;
    loading: boolean;
    login: (email: string, password: string) => Promise<void>;
    register: (data: Parameters<typeof authApi.register>[0]) => Promise<void>;
    logout: () => Promise<void>;
    refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
    user: null,
    loading: true,
    login: async () => { },
    register: async () => { },
    logout: async () => { },
    refreshUser: async () => { },
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<User>(null);
    const [loading, setLoading] = useState(true);

    const refreshUser = useCallback(async () => {
        try {
            if (!tokenStore.getAccess()) {
                setUser(null);
                return;
            }
            const me = await authApi.getMe();
            setUser(me);
        } catch {
            setUser(null);
        }
    }, []);

    useEffect(() => {
        refreshUser().finally(() => setLoading(false));
    }, [refreshUser]);

    const login = useCallback(async (email: string, password: string) => {
        const data = await authApi.login(email, password);
        setUser(data.user);
    }, []);

    const register = useCallback(async (data: Parameters<typeof authApi.register>[0]) => {
        const result = await authApi.register(data);
        setUser(result.user);
    }, []);

    const logout = useCallback(async () => {
        await authApi.logout();
        setUser(null);
    }, []);

    return (
        <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}

// Legacy compatibility alias for code that uses useFirebase()
// This allows gradual migration without breaking existing callers
export function useFirebase() {
    const { user } = useAuth();
    return {
        user,
        firestore: null, // Firebase Firestore removed — use API hooks instead
        auth: null,
        storage: null,
    };
}
